import { applicationReducer } from 'app-webtech-core/modules/application/reducers';
import { persistCombineReducers } from 'redux-persist';
import { createWhitelistFilter } from 'redux-persist-transform-filter';
import storage from 'redux-persist/lib/storage';
import getStoredState from 'redux-persist/lib/getStoredState';
import { deviceReducer } from 'app-webtech-core/modules/device/reducers';
import { guideReducer } from 'app-webtech-core/modules/guide/reducers';
import { layoutReducer } from 'app-webtech-core/modules/layout/reducers';
import { linearChannelsReducer } from 'app-webtech-core/modules/linearChannels/reducers';
import { playerReducer } from 'app-webtech-core/modules/player/reducers';
import { playbackSessionReducer } from 'app-webtech-core/modules/playbackSession/reducers';
import { vodContentReducer } from 'app-webtech-core/modules/vodContent/reducers';
import { vodGuideReducer } from 'app-webtech-core/modules/vodGuide/reducers';
import { userAccountReducer } from 'app-webtech-core/modules/userAccount/reducers';
import { pairedDevicesReducer } from 'app-webtech-core/modules/pairedDevices/reducers';
import { podTrackerAdsReducer } from 'app-webtech-core/modules/podTracker/ads/reducers';
import { updateState } from './updateState';
import { metaExtractor, payloadExtractor } from './payloadExtractor';
import { createReducer } from './createReducer';
import { createFSMReducer } from './fsm';
import { pageReducerCreator } from '../modules/application/reducers/page';
import { getDefaultPage } from '../store/routes';
import { ratingsDescriptorsReducer } from 'app-webtech-core/modules/ratingsDescriptors/reducers';
import { ratingsReducer } from 'app-webtech-core/modules/ratings/reducers';

const getPersistConfig = ({
  whitelist: customWhitelist = [],
  transforms: customTransforms = [],
} = {}) => ({
  key: 'root',
  whitelist: [
    'application',
    'device',
    'guide',
    'userAccount',
    'player',
    'podTrackerAds',
    ...customWhitelist,
  ],
  transforms: [
    createWhitelistFilter('application', ['featureDiscovery', 'getAppBannerDismissed']),
    createWhitelistFilter('guide', [
      'selectedIndex',
      'currentPlayingIndex',
      'previousPlayingIndex',
    ]),
    createWhitelistFilter('userAccount', ['accessToken', 'firstVisitedAt']),
    createWhitelistFilter('player', ['mute', 'volume', 'volumeBeforeMute']),
    createWhitelistFilter('podTrackerAds', [
      'viewedVodAdPods',
      'vodLastPosition',
      'continueWatching',
    ]),
    ...customTransforms,
  ],
  storage,
  version: process.env.CORE_VERSION,
});

export const getPersistedState = (customPersistConfig) =>
  getStoredState(getPersistConfig(customPersistConfig));

export const getRootReducer = ({
  routerReducer,
  routesMap,
  customReducers = {},
  customPersistConfig,
}) => {
  const defaultPage = getDefaultPage(routesMap);

  const mergedReducers = {
    application: applicationReducer,
    device: deviceReducer,
    guide: guideReducer,
    layout: layoutReducer,
    linearChannels: linearChannelsReducer,
    vodContent: vodContentReducer,
    player: playerReducer,
    playbackSession: playbackSessionReducer,
    userAccount: userAccountReducer,
    pairedDevices: pairedDevicesReducer,
    podTrackerAds: podTrackerAdsReducer,
    vodGuide: vodGuideReducer,
    ...customReducers,
    location: routerReducer,
    page: customReducers.page || pageReducerCreator(routesMap, defaultPage),
    ratings: ratingsReducer,
    ratingDescriptors: ratingsDescriptorsReducer,
  };
  // TODO: Remove `page` reducer on version 2.0.0
  console.warn(
    'Deprecation warning: `page` reducer will be deprecated on version 2.0.0, ' +
      'use `selectors.location.page(state)` to get the current page instead.',
  );

  return persistCombineReducers(getPersistConfig(customPersistConfig), mergedReducers);
};

export { updateState, metaExtractor, payloadExtractor, createReducer, createFSMReducer };
